"use client";

import { Users, Palette, HandHeart, Globe, TrendingUp, Truck, ShieldCheck, ChartBar as BarChart3, Zap } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Badge } from "@/components/ui/badge";
export default function SupplierBenefits() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const cue = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const benefits = [{
    icon: Globe,
    title: "Global Buyer Access",
    description: "Connect instantly with verified international buyers actively seeking your specific product categories and inventory.",
    trustText: "5,000+ Verified Buyers across 45 countries",
    badge: "Verified Network",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/global_logistics_network.png"
  }, {
    icon: TrendingUp,
    title: "Demand Forecasting",
    description: "Leverage advanced analytics to predict market trends and optimize your production schedules for maximum ROI.",
    trustText: "94% Data Accuracy in 2023 Market Reports",
    badge: "AI Powered",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/market_data_analysis.webp"
  }, {
    icon: Truck,
    title: "Logistics Partnership",
    description: "Access our network of certified freight forwarders and customs experts to streamline your global distribution.",
    trustText: "ISO 9001 Certified Logistics Partners",
    badge: "Secure Transit",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/shipping_container_port.webp"
  }, {
    icon: ShieldCheck,
    title: "Payment Security",
    description: "Trade with confidence using our DAFZA-compliant escrow systems and comprehensive fraud protection protocols.",
    trustText: "DAFZA Compliant & Escrow Protected",
    badge: "Bank-Grade",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/secure_digital_payment.png"
  }, {
    icon: BarChart3,
    title: "Market Intelligence",
    description: "Gain a competitive edge with real-time pricing data and competitor analysis tailored to your industry sector.",
    trustText: "Direct Bloomberg & Reuters Data Feeds",
    badge: "Premium Intel",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/corporate_strategy_meeting.webp"
  }, {
    icon: Zap,
    title: "Instant Quote System",
    description: "Respond to RFQs in seconds with our automated quoting engine, ensuring you never miss a high-value opportunity.",
    trustText: "99.9% Uptime & <2s Response Guarantee",
    badge: "High Speed",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/modern_office_technology.webp"
  }];
  return <section data-section-id="16231" id="supplierbenefitsshowcase" className="bg-white py-28 md:py-36">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 md:px-10">
        <div ref={intro.ref} className="mx-auto max-w-[640px] text-center" style={{
        opacity: intro.isVisible ? 1 : 0,
        transform: intro.isVisible ? "translateY(0)" : "translateY(16px)",
        transition: "opacity 520ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 520ms cubic-bezier(0.22, 0.61, 0.36, 1)"
      }}>
          <h2 className="font-default text-[#1a3a52]" style={{
          fontSize: "clamp(2rem, 3.8vw, 3.5rem)",
          fontWeight: 600,
          letterSpacing: "-0.018em",
          lineHeight: 1.08
        }}>
            Why leading suppliers choose{" "}
            <em className="not-italic text-[#d4a574]" style={{
            fontWeight: 600
          }}>
              KTEX
            </em>
            .
          </h2>
          <p className="mt-4 text-[#1a1a1a] font-default" style={{
          fontSize: "clamp(1.0625rem, 1.25vw, 1.1875rem)",
          fontWeight: 400,
          letterSpacing: "-0.005em",
          lineHeight: 1.6
        }}>
            We provide the infrastructure to scale your export operations, ensuring your products reach the right markets with efficiency and trust.
          </p>
        </div>

        <div ref={grid.ref} className="mt-14 md:mt-[56px] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
          {benefits.map((benefit, i) => {
          const Icon = benefit.icon;
          const isMiddle = i === 1;
          return <div data-index={i} key={i} className="group relative bg-white border border-[#e0e0e0] p-8 transition-all duration-300 ease-out hover:-translate-y-[3px] hover:border-[#1a3a52]/30 shadow-sm hover:shadow-md overflow-hidden" style={{
            borderRadius: "8px",
            opacity: grid.isVisible ? 1 : 0,
            transform: grid.isVisible ? "translateY(0)" : "translateY(16px)",
            transition: `opacity 520ms cubic-bezier(0.22, 0.61, 0.36, 1) ${i * 70}ms, transform 520ms cubic-bezier(0.22, 0.61, 0.36, 1) ${i * 70}ms, border-color 280ms ease-out, translate 280ms ease-out`
          }}>
                {}
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500 pointer-events-none bg-cover bg-center bg-no-repeat bg-[url(${benefit.image})]`} />

                {isMiddle && <div ref={cue.ref} className="absolute -top-3 -right-2 pointer-events-none z-10" aria-hidden="true">
                    <svg width="72" height="56" viewBox="0 0 72 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M68 6 C 52 4, 36 14, 24 28 C 20 33, 18 38, 18 44" stroke="#d4a574" strokeWidth="1.75" strokeLinecap="round" fill="none" strokeDasharray="200" strokeDashoffset={cue.isVisible ? 0 : 200} style={{
                  transition: "stroke-dashoffset 700ms ease-out 200ms"
                }} />
                      <path d="M18 44 L 12 36 M 18 44 L 26 40" stroke="#d4a574" strokeWidth="1.75" strokeLinecap="round" fill="none" strokeDasharray="40" strokeDashoffset={cue.isVisible ? 0 : 40} style={{
                  transition: "stroke-dashoffset 400ms ease-out 800ms"
                }} />
                    </svg>
                  </div>}

                <div className="flex items-start justify-between mb-5">
                  <Icon className="text-[#1a3a52] transition-all duration-300 ease-out" size={44} strokeWidth={1.5} />
                  <Badge className="bg-[#d4a574]/10 text-[#d4a574] border-[#d4a574]/20 hover:bg-[#d4a574]/20 rounded-md font-medium text-[10px] uppercase tracking-wider">
                    {benefit.badge}
                  </Badge>
                </div>

                <h3 className="font-default text-[#1a3a52]" style={{
              fontSize: "1.375rem",
              fontWeight: 600,
              letterSpacing: "-0.012em",
              lineHeight: 1.2
            }}>
                  {benefit.title}
                </h3>
                
                <p className="mt-3 text-[#1a1a1a] font-default" style={{
              fontSize: "0.9375rem",
              fontWeight: 400,
              lineHeight: 1.6
            }}>
                  {benefit.description}
                </p>

                <div className="mt-6 pt-5 border-t border-[#e0e0e0]/60 flex items-center gap-2">
                  <ShieldCheck size={14} className="text-[#d4a574]" />
                  <span className="text-[11px] font-semibold text-[#1a3a52]/70 uppercase tracking-tight">
                    {benefit.trustText}
                  </span>
                </div>
              </div>;
        })}
        </div>
      </div>

      <style>{`
        #supplierbenefitsshowcase .group:hover svg:not([aria-hidden="true"] *) {
          stroke-width: 1.75;
        }
      `}</style>
    </section>;
}